import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePromptStore } from '@/stores/promptStore';
import type { Prompt } from '@/types/prompt';
import { toast } from 'sonner';

// 导入分类数据
import categoriesData from '@/data/categories.json';

interface PromptEditorProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
}

export function PromptEditor({ prompt, isOpen, onClose }: PromptEditorProps) {
  const { createPrompt, updatePrompt } = usePromptStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    content: '',
    category: '',
    tags: '',
    permission: 'basic' as 'basic' | 'premium' | 'admin',
    author: '',
  });

  // 当 prompt 改变时更新表单
  useEffect(() => {
    if (prompt) {
      setFormData({
        id: prompt.id,
        title: prompt.title,
        description: prompt.description,
        content: prompt.content,
        category: prompt.category,
        tags: prompt.tags?.join(', ') || '',
        permission: prompt.permission,
        author: prompt.author || '',
      });
    } else {
      // 重置表单
      setFormData({
        id: '',
        title: '',
        description: '',
        content: '',
        category: '',
        tags: '',
        permission: 'basic',
        author: '',
      });
    }
  }, [prompt]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.id || !formData.title || !formData.description || !formData.content || !formData.category) {
      toast.error('请填写所有必填字段');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      };

      if (prompt) {
        // 更新现有 Prompt
        await updatePrompt(prompt.id, data);
        toast.success('Prompt 更新成功');
      } else {
        // 创建新 Prompt
        await createPrompt(data as any);
        toast.success('Prompt 创建成功');
      }

      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {prompt ? '编辑 Prompt' : '创建 Prompt'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ID */}
          <div className="space-y-2">
            <Label htmlFor="id">
              ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="id"
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value })}
              placeholder="例如: code-review-001"
              disabled={!!prompt} // 编辑时不允许修改 ID
              required
            />
            <p className="text-xs text-muted-foreground">
              只能包含字母、数字、连字符和下划线
            </p>
          </div>

          {/* 标题 */}
          <div className="space-y-2">
            <Label htmlFor="title">
              标题 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Prompt 标题"
              required
            />
          </div>

          {/* 描述 */}
          <div className="space-y-2">
            <Label htmlFor="description">
              描述 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="简短描述这个 Prompt 的用途"
              required
            />
          </div>

          {/* 分类 */}
          <div className="space-y-2">
            <Label htmlFor="category">
              分类 <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="选择分类" />
              </SelectTrigger>
              <SelectContent>
                {categoriesData.map((cat: any) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 权限 */}
          <div className="space-y-2">
            <Label htmlFor="permission">
              权限 <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.permission}
              onValueChange={(value: any) => setFormData({ ...formData, permission: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic (所有用户)</SelectItem>
                <SelectItem value="premium">Premium (高级用户)</SelectItem>
                <SelectItem value="admin">Admin (仅管理员)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 标签 */}
          <div className="space-y-2">
            <Label htmlFor="tags">标签</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="用逗号分隔，例如: 代码审查, 最佳实践"
            />
          </div>

          {/* 作者 */}
          <div className="space-y-2">
            <Label htmlFor="author">作者</Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              placeholder="作者名称（可选）"
            />
          </div>

          {/* 内容 */}
          <div className="space-y-2">
            <Label htmlFor="content">
              内容 <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Prompt 的完整内容（支持 Markdown）"
              className="w-full min-h-[300px] px-3 py-2 text-sm rounded-md border border-input bg-background"
              required
            />
            <p className="text-xs text-muted-foreground">
              支持 Markdown 格式
            </p>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              取消
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? '保存中...' : prompt ? '更新' : '创建'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

